import { useEffect, useRef } from "react";

/**
 * Keep the screen awake while `active` is true using the Wake Lock API.
 * Falls back gracefully on browsers without support (Safari < 16.4).
 * Re-acquires the lock if the page is hidden and then becomes visible again
 * (the browser releases the wake lock automatically on visibilitychange).
 */
export const useWakeLock = (active) => {
  const sentinelRef = useRef(null);

  useEffect(() => {
    if (!active) {
      // release if held
      const s = sentinelRef.current;
      if (s && typeof s.release === "function") {
        s.release().catch(() => {});
        sentinelRef.current = null;
      }
      return undefined;
    }
    if (!("wakeLock" in navigator)) return undefined;

    let cancelled = false;
    const acquire = async () => {
      try {
        if (cancelled) return;
        // eslint-disable-next-line
        const s = await navigator.wakeLock.request("screen");
        sentinelRef.current = s;
        s.addEventListener?.("release", () => {
          // released by browser (e.g. tab hidden) — try to re-acquire on visibility
        });
      } catch { /* permission denied or unsupported */ }
    };

    acquire();
    const onVisibility = () => {
      if (document.visibilityState === "visible") acquire();
    };
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisibility);
      const s = sentinelRef.current;
      if (s && typeof s.release === "function") s.release().catch(() => {});
      sentinelRef.current = null;
    };
  }, [active]);
};
